import type React from "react";

import { X } from "lucide-react";
import type { BankTransfersData } from "../../../interface/interfaces";

interface TransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: BankTransfersData | null;
  formatDate: (dateString: string) => string;
  formatAmount: (amount: number, currency: string) => string;
  getStatusColor: (status: string) => string;
}

const BankTransferModal: React.FC<TransactionModalProps> = ({
  isOpen,
  onClose,
  transaction,
  formatDate,
  formatAmount,
  getStatusColor,
}) => {
  if (!isOpen || !transaction) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            Transaction Details
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-2 transition-all duration-200"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="block text-sm font-semibold text-gray-600 mb-2">
                  Reference
                </label>
                <p className="text-gray-900 font-mono text-lg">
                  {transaction.reference}
                </p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="block text-sm font-semibold text-gray-600 mb-2">
                  Customer ID
                </label>
                <p className="text-gray-900 font-medium">
                  {transaction.customerId}
                </p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="block text-sm font-semibold text-gray-600 mb-2">
                  Amount
                </label>
                <p className="text-gray-900 font-bold text-xl">
                  {formatAmount(transaction.amount, transaction.currency)}
                </p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="block text-sm font-semibold text-gray-600 mb-2">
                  Currency
                </label>
                <p className="text-gray-900 font-medium">
                  {transaction.currency}
                </p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="block text-sm font-semibold text-gray-600 mb-2">
                  Status
                </label>
                <span
                  className={`px-4 py-2 rounded-full text-sm font-bold ${getStatusColor(
                    transaction.status
                  )}`}
                >
                  {transaction.status}
                </span>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="block text-sm font-semibold text-gray-600 mb-2">
                  Request ID
                </label>
                <p className="text-gray-900 font-mono text-sm break-all">
                  {transaction.requestId}
                </p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="block text-sm font-semibold text-gray-600 mb-2">
                  Merchant ID
                </label>
                <p className="text-gray-900 font-medium">
                  {transaction.merchantId}
                </p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="block text-sm font-semibold text-gray-600 mb-2">
                  Date Created
                </label>
                <p className="text-gray-900 font-medium">
                  {formatDate(transaction.dateCreated)}
                </p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="block text-sm font-semibold text-gray-600 mb-2">
                  Date Updated
                </label>
                <p className="text-gray-900 font-medium">
                  {transaction.dateUpdated
                    ? formatDate(transaction.dateUpdated)
                    : "Not updated"}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-4 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-6 py-3 text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 font-medium shadow-sm hover:shadow-md min-w-[120px]"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default BankTransferModal;
